import type { ReactNode } from 'react'
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts'
import { format } from 'date-fns'
import type { UsageStatsRow } from '@/hooks/useAdminUsageStats'

type UsageMetricId =
  | 'users'
  | 'groups'
  | 'threads'
  | 'comments'
  | 'articles'
  | 'articleReads'
  | 'linkFollows'

interface UsageGrowthChartProps {
  rows: UsageStatsRow[]
  fromDate: string
  toDate: string
  visibleMetrics: Record<UsageMetricId, boolean>
}

/** Format date for X axis */
function formatAxisDate(iso: string): string {
  return format(new Date(iso), 'd MMM')
}

const LINE_CONFIG: Record<
  UsageMetricId,
  { dataKey: string; name: string; stroke: string; strokeDasharray: string }
> = {
  users: { dataKey: 'users', name: 'New users', stroke: 'var(--color-info)', strokeDasharray: '0' },
  groups: { dataKey: 'groups', name: 'New groups', stroke: 'var(--color-success)', strokeDasharray: '8 4' },
  threads: { dataKey: 'threads', name: 'New threads', stroke: 'var(--text-link-visited)', strokeDasharray: '5 5' },
  comments: { dataKey: 'comments', name: 'New comments', stroke: 'var(--color-warning)', strokeDasharray: '2 2' },
  articles: { dataKey: 'articles', name: 'New articles', stroke: '#0EA5E9', strokeDasharray: '1 4' },
  articleReads: { dataKey: 'articleReads', name: 'Articles read', stroke: 'var(--color-error)', strokeDasharray: '4 2 1 2' },
  linkFollows: { dataKey: 'linkFollows', name: 'Links followed', stroke: 'var(--color-focus)', strokeDasharray: '1 2 4 2' },
}

/** Chart uses theme colours and stroke patterns so lines are not distinguished by colour alone. */
export default function UsageGrowthChart({ rows, fromDate, toDate, visibleMetrics }: UsageGrowthChartProps) {
  const chartData = rows.map((r) => ({
    date: r.stat_date,
    dateLabel: formatAxisDate(r.stat_date),
    users: r.new_users,
    groups: r.new_groups,
    threads: r.new_threads,
    comments: r.new_comments,
    articles: r.new_articles,
    articleReads: r.article_reads,
    linkFollows: r.link_follows,
  })).reverse()

  if (chartData.length === 0) {
    return (
      <p style={{ color: 'var(--text-secondary)' }} className="p-4">
        No data to display in chart.
      </p>
    )
  }

  return (
    <div className="w-full" style={{ minHeight: 320 }}>
      <p className="sr-only" id="usage-chart-desc">
        Line chart of daily new users, groups, threads, comments, articles, article reads, and link follows from {fromDate} to {toDate}.
        Each series has a distinct line style (solid, long dash, medium dash, short dash, dot dash, and two additional patterns).
      </p>
      <ResponsiveContainer width="100%" height={320}>
        <LineChart
          data={chartData}
          margin={{ top: 8, right: 8, left: 8, bottom: 8 }}
          aria-describedby="usage-chart-desc"
        >
          <CartesianGrid
            strokeDasharray="3 3"
            stroke="var(--bg-tertiary)"
          />
          <XAxis
            dataKey="dateLabel"
            tick={{ fill: 'var(--text-primary)', fontSize: 12 }}
            stroke="var(--text-secondary)"
          />
          <YAxis
            tick={{ fill: 'var(--text-primary)', fontSize: 12 }}
            stroke="var(--text-secondary)"
            allowDecimals={false}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: 'var(--bg-primary)',
              border: '2px solid var(--bg-tertiary)',
              color: 'var(--text-primary)',
              opacity: 1,
            }}
            wrapperStyle={{ opacity: 1 }}
            labelStyle={{ color: 'var(--text-primary)' }}
            formatter={(value, name) => [(typeof value === 'number' ? value : 0).toLocaleString(), name] as [ReactNode, ReactNode]}
            labelFormatter={(label) => `Date: ${label}`}
          />
          <Legend
            wrapperStyle={{
              paddingTop: 8,
              paddingRight: 8,
              paddingBottom: 8,
              paddingLeft: 8,
              backgroundColor: 'var(--bg-primary)',
              opacity: 1,
            }}
            formatter={(value) => <span style={{ color: 'var(--text-primary)' }}>{value}</span>}
          />
          {(Object.keys(LINE_CONFIG) as UsageMetricId[]).map(
            (id) =>
              visibleMetrics[id] && (
                <Line
                  key={id}
                  type="monotone"
                  dataKey={LINE_CONFIG[id].dataKey}
                  name={LINE_CONFIG[id].name}
                  stroke={LINE_CONFIG[id].stroke}
                  strokeWidth={2}
                  strokeDasharray={LINE_CONFIG[id].strokeDasharray}
                  dot={{ fill: LINE_CONFIG[id].stroke, strokeWidth: 0 }}
                  connectNulls
                />
              )
          )}
        </LineChart>
      </ResponsiveContainer>
    </div>
  )
}
